import {
  AbsoluteFill,
  Sequence,
  OffthreadVideo,
  Audio,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
} from "remotion";
import React from "react";
import { z } from "zod";
import { toHttpCdn } from "../utils";

// ===== Schema 定义 =====
export const SpeedSegmentSchema = z.object({
  startTime: z.number().describe("加速开始时间（秒）"),
  endTime: z.number().describe("加速结束时间（秒）"),
  rate: z.number().default(2).describe("播放倍率，2 表示 2 倍速"),
});

export const CutSegmentSchema = z.object({
  startTime: z.number().describe("移除片段开始时间（秒）"),
  endTime: z.number().describe("移除片段结束时间（秒）"),
});

export const TextOverlaySchema = z.object({
  startTime: z.number().describe("文案开始展示时间（秒）"),
  text: z.string().describe("文案内容，支持 \\n 换行"),
});

export const VideoItemSchemaV8 = z.object({
  url: z.string().describe("视频 URL"),
  duration: z.number().optional().describe("视频时长（秒），可选，不填则自动获取"),
  startFrom: z.number().default(0).describe("视频开始播放的时间点（秒），用于截取精彩片段"),
  speedSegments: z.array(SpeedSegmentSchema).optional().describe("加速片段配置"),
  cutSegments: z.array(CutSegmentSchema).optional().describe("移除片段配置，支持多个时间段"),
});

export const HighlightVideoV8PropsSchema = z.object({
  videos: z.array(VideoItemSchemaV8).describe("视频列表，按顺序播放"),
  audioUrl: z.string().optional().describe("背景音乐 URL"),
  audioStartFrom: z.number().default(0).describe("音频起始时间（秒）"),
  audioVolume: z.number().default(0.45).describe("背景音乐音量 (0-1)"),
  videoVolume: z.number().default(0.1).describe("视频原声音量 (0-1)，默认10%"),
  // 视频放大倍数（用于裁剪左右边缘）
  videoScale: z.number().default(1.6).describe("视频放大倍数，>1 会裁剪左右"),
  // 卡点转场：指定音乐节拍时间点（相对于 audioStartFrom 的偏移）
  // 例如：[0, 3.5, 7, 10.5] 表示在这些时间点切换视频
  beatTimestamps: z.array(z.number()).optional().describe("音乐节拍时间点数组（秒），用于卡点转场"),
  // 自动节拍对齐：每个视频完整播放后，切换点自动对齐到最近的节拍
  autoBeatAlign: z.boolean().default(false).describe("是否自动将切换点对齐到节拍"),
  bpm: z.number().default(99.4).describe("音乐 BPM，用于自动节拍对齐"),
  // 首尾呼应：在结尾再播放第一个视频的另一部分，让循环更平滑
  loopBack: z.boolean().default(false).describe("是否在结尾回到第一个视频"),
  loopBackStartFrom: z.number().default(0).describe("首尾呼应时，结尾视频从第几秒开始"),
  // 高光预览：将视频最后 x 秒放到开头，慢镜头 + 放大播放
  previewEnabled: z.boolean().default(false).describe("是否启用高光预览"),
  previewDuration: z.number().default(3).describe("预览片段的原始时长（秒），从视频末尾截取"),
  previewSlowRate: z.number().default(0.5).describe("慢放倍率，0.5 表示 2 倍慢放"),
  previewZoom: z.number().default(1.8).describe("放大倍数"),
  previewFocusX: z.number().default(50).describe("聚焦点 X 位置（百分比，50 为中间）"),
  previewFocusY: z.number().default(30).describe("聚焦点 Y 位置（百分比，30 为上部）"),
  title: z.string().default("DOTA2").describe("上方标题"),
  subtitle: z.string().default("超神集锦").describe("上方副标题"),
  brandName: z.string().default("@超神时刻").describe("下方品牌名"),
  brandSubtitle: z.string().default("GODLIKE MOMENTS").describe("下方品牌副标题"),
  // 时间段文案配置
  textOverlays: z.array(TextOverlaySchema).optional().describe("时间段文案配置，按开始时间切换"),
});

export type HighlightVideoV8Props = z.infer<typeof HighlightVideoV8PropsSchema>;

// ===== 常量 =====
const FPS = 30;
const TRANSITION_DURATION = 15;

// 画布尺寸
const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1920;

// 视频区域高度
const VIDEO_HEIGHT = 1400;
const TOP_BANNER_HEIGHT = (CANVAS_HEIGHT - VIDEO_HEIGHT) / 2; // 360
// 蒙层延伸像素，覆盖黑边
const OVERLAY_EXTEND = 5;

// ===== 上方品牌区 =====
const TopBanner: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
  const frame = useCurrentFrame();

  const glowIntensity = interpolate(frame % 60, [0, 30, 60], [0.5, 1, 0.5]);
  const particleOffset = (frame * 2) % 200;
  const floatY = interpolate(frame % 90, [0, 45, 90], [0, -5, 0]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: TOP_BANNER_HEIGHT + OVERLAY_EXTEND,
        background: "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* 动态背景线条 */}
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: `${(i * 25 + particleOffset) % 120 - 20}%`,
            width: 2,
            height: "100%",
            background: `linear-gradient(180deg, transparent, rgba(255,215,0,${0.1 + i * 0.05}), transparent)`,
            transform: "skewX(-20deg)",
          }}
        />
      ))}

      {/* 标题 */}
      <div style={{ transform: `translateY(${floatY}px)`, textAlign: "center" }}>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 130,
            fontWeight: "bold",
            color: "#FF4444",
            textShadow: `
              0 0 40px rgba(255,68,68,${glowIntensity}),
              0 0 80px rgba(255,68,68,${glowIntensity * 0.5}),
              0 0 120px rgba(255,68,68,${glowIntensity * 0.3}),
              4px 4px 0 #000
            `,
            letterSpacing: 15,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 90,
            color: "#FFD700",
            marginTop: 20,
            textShadow: `0 0 30px rgba(255,215,0,${glowIntensity})`,
            letterSpacing: 12,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 底部装饰线 */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,68,68,${glowIntensity}) 20%, 
            rgba(255,215,0,${glowIntensity}) 50%, 
            rgba(255,68,68,${glowIntensity}) 80%, 
            transparent 100%)`,
        }}
      />
    </div>
  );
};

// ===== 下方品牌区 =====
const BottomBanner: React.FC<{ brandName: string; brandSubtitle: string }> = ({
  brandName,
  brandSubtitle,
}) => {
  const frame = useCurrentFrame();

  const breathe = interpolate(frame % 120, [0, 60, 120], [1, 1.05, 1]);
  const glowIntensity = interpolate(frame % 60, [0, 30, 60], [0.6, 1, 0.6]);

  // 浮动粒子
  const particles = [...Array(8)].map((_, i) => {
    const speed = 1 + (i % 3) * 0.5;
    const offset = (frame * speed + i * 45) % 360;
    const x = 50 + Math.sin((offset * Math.PI) / 180) * 40;
    const y = 50 + Math.cos((offset * Math.PI) / 180) * 25;
    const size = 3 + (i % 3) * 2;
    const opacity = 0.3 + (i % 4) * 0.15;
    return { x, y, size, opacity };
  });

  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: TOP_BANNER_HEIGHT + OVERLAY_EXTEND,
        background: "linear-gradient(0deg, #0a0a0a 0%, #1a1a2e 100%)",
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* 顶部装饰线 */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,215,0,${glowIntensity}) 20%, 
            rgba(255,100,100,${glowIntensity}) 50%, 
            rgba(255,215,0,${glowIntensity}) 80%, 
            transparent 100%)`,
        }}
      />

      {/* 浮动粒子 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: i % 2 === 0 ? "#FFD700" : "#FF6347",
            opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 2}px ${i % 2 === 0 ? "#FFD700" : "#FF6347"}`,
          }}
        />
      ))}

      {/* 主标题 */}
      <div style={{ transform: `scale(${breathe})`, textAlign: "center", zIndex: 10 }}>
        <span
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 90,
            fontWeight: "bold",
            background: "linear-gradient(90deg, #FFD700, #FFA500, #FF6347)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: `drop-shadow(0 0 20px rgba(255,215,0,${glowIntensity}))`,
            letterSpacing: 4,
          }}
        >
          {brandName}
        </span>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 36,
            color: "rgba(255,255,255,0.7)",
            marginTop: 25,
            letterSpacing: 12,
          }}
        >
          {brandSubtitle}
        </div>
      </div>
    </div>
  );
};

// ===== 转场效果类型 =====
type TransitionType = "scale" | "flash" | "none";

// ===== 闪白转场效果 =====
const FlashTransition: React.FC<{ durationFrames: number }> = ({ durationFrames }) => {
  const frame = useCurrentFrame();
  
  const opacity = interpolate(
    frame,
    [0, durationFrames * 0.3, durationFrames],
    [1, 0.8, 0],
    { extrapolateRight: "clamp" }
  );

  if (frame >= durationFrames) return null;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "white",
        opacity,
        zIndex: 100,
      }}
    />
  );
};

// ===== 全屏单视频组件（带品牌区） =====
const FullScreenVideo: React.FC<{
  src: string;
  startFrom?: number; // 视频开始时间（秒）
  title: string;
  subtitle: string;
  brandName: string;
  brandSubtitle: string;
  showFlash?: boolean; // 是否显示闪白转场
  playbackRate?: number; // 播放速率
  zoom?: number; // 放大倍数
  zoomAnimated?: boolean; // 是否启用 zoom 动画
  zoomDurationFrames?: number; // zoom 动画时长（帧）
  focusX?: number; // 聚焦点 X（百分比）
  focusY?: number; // 聚焦点 Y（百分比）
  videoScale?: number; // 视频基础放大倍数
  overlayText?: string; // 覆盖文案（支持换行）
  textOverlays?: z.infer<typeof TextOverlaySchema>[]; // 时间段文案配置
  outputTimeOffset?: number; // 这个片段在输出时间轴上的开始时间（秒）
  volume?: number; // 视频音量 (0-1)
}> = ({
  src, 
  startFrom = 0, 
  title, 
  subtitle, 
  brandName, 
  brandSubtitle, 
  showFlash = false,
  playbackRate = 1,
  zoom = 1,
  zoomAnimated = false,
  zoomDurationFrames = 90, // 默认 3 秒
  focusX = 50,
  focusY = 50,
  videoScale = 1,
  overlayText,
  textOverlays,
  outputTimeOffset = 0,
  volume = 0.1,
}) => {
  const frame = useCurrentFrame();
  
  // 根据输出时间轴计算应该显示的文案
  const currentOutputTime = outputTimeOffset + frame / FPS;
  const dynamicOverlayText = React.useMemo(() => {
    if (overlayText) return overlayText; // 优先使用直接传入的文案
    if (!textOverlays || textOverlays.length === 0) return undefined;
    
    const sorted = [...textOverlays].sort((a, b) => a.startTime - b.startTime);
    let currentText: string | undefined;
    for (const overlay of sorted) {
      if (overlay.startTime <= currentOutputTime) {
        currentText = overlay.text;
      } else {
        break;
      }
    }
    return currentText;
  }, [overlayText, textOverlays, currentOutputTime]);

  const scale = interpolate(frame, [0, TRANSITION_DURATION], [1.05, 1], {
    extrapolateRight: "clamp",
  });

  // 如果启用 zoom 动画，从 1 渐变到目标 zoom
  const currentZoom = zoomAnimated
    ? interpolate(frame, [0, zoomDurationFrames], [1, zoom], { extrapolateRight: "clamp" })
    : zoom;

  // 计算放大后的偏移量，使聚焦点居中
  const translateX = (50 - focusX) * (currentZoom - 1) / currentZoom * 100 / 50;
  const translateY = (50 - focusY) * (currentZoom - 1) / currentZoom * 100 / 50;

  // 总放大倍数 = 动态缩放 * zoom
  const totalScale = scale * currentZoom;

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* 闪白转场 */}
      {showFlash && <FlashTransition durationFrames={8} />}

      {/* 上方品牌区 */}
      <TopBanner title={title} subtitle={brandSubtitle} />

      {/* 视频区域（居中） */}
      <div
        style={{
          position: "absolute",
          top: TOP_BANNER_HEIGHT,
          left: 0,
          width: "100%",
          height: VIDEO_HEIGHT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          overflow: "hidden",
        }}
      >
        <OffthreadVideo
          src={toHttpCdn(src)}
          startFrom={Math.floor(startFrom * FPS)}
          playbackRate={playbackRate}
          volume={volume}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `scale(${totalScale}) translate(${translateX}%, ${translateY}%)`,
          }}
        />
        {/* 加速播放提示 */}
        {playbackRate > 1 && (
          <div
            style={{
              position: "absolute",
              top: 320,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              padding: "14px 32px",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            {/* 转圈 Loading */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "5px solid rgba(255, 255, 255, 0.3)",
                borderTopColor: "#fff",
                transform: `rotate(${frame * 12}deg)`,
              }}
            />
            <span style={{ 
              fontSize: 56, 
              color: "#fff", 
              fontWeight: "bold",
            }}>
              加速 ⏩️ {playbackRate}x
            </span>
          </div>
        )}
        {/* 文案覆盖（非加速时展示） */}
        {playbackRate <= 1 && dynamicOverlayText && (
          <div
            style={{
              position: "absolute",
              top: 320,
              left: 36,
              right: 36,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "16px 36px",
              borderRadius: 12,
              textAlign: "center",
            }}
          >
            {dynamicOverlayText.split("\n").map((line, i) => (
              <div
                key={i}
                style={{
                  fontSize: 48,
                  color: "#fff",
                  fontWeight: "bold",
                  lineHeight: 1.4,
                }}
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 下方品牌区 */}
      <BottomBanner brandName={brandName} brandSubtitle={subtitle} />
    </AbsoluteFill>
  );
};

// ===== 自动计算节拍对齐的时间点 =====
export const calculateAutoBeatTimestampsV8 = (
  videos: z.infer<typeof VideoItemSchemaV8>[],
  bpm: number = 99.4
): number[] => {
  const beatInterval = 60 / bpm;
  let cumulative = 0;
  const timestamps = [0];
  
  videos.forEach((video) => {
    cumulative += video.duration || 0;
    // 对齐到最近的节拍
    const nearestBeat = Math.round(cumulative / beatInterval) * beatInterval;
    timestamps.push(parseFloat(nearestBeat.toFixed(2)));
  });
  
  return timestamps;
};

// ===== 计算需要保留的时间区间（排除 cutSegments） =====
const getRetainedIntervals = (
  startFrom: number,
  endTime: number,
  cutSegments?: z.infer<typeof CutSegmentSchema>[]
): { start: number; end: number }[] => {
  if (!cutSegments || cutSegments.length === 0) {
    return [{ start: startFrom, end: endTime }];
  }

  // 排序并合并重叠的 cut 区间
  const sortedCuts = [...cutSegments].sort((a, b) => a.startTime - b.startTime);
  const mergedCuts: { start: number; end: number }[] = [];
  
  for (const cut of sortedCuts) {
    const cutStart = Math.max(cut.startTime, startFrom);
    const cutEnd = Math.min(cut.endTime, endTime);
    if (cutStart >= cutEnd) continue;
    
    if (mergedCuts.length === 0 || cutStart > mergedCuts[mergedCuts.length - 1].end) {
      mergedCuts.push({ start: cutStart, end: cutEnd });
    } else {
      mergedCuts[mergedCuts.length - 1].end = Math.max(mergedCuts[mergedCuts.length - 1].end, cutEnd);
    }
  }

  // 计算保留的区间
  const retained: { start: number; end: number }[] = [];
  let current = startFrom;
  
  for (const cut of mergedCuts) {
    if (current < cut.start) {
      retained.push({ start: current, end: cut.start });
    }
    current = cut.end;
  }
  
  if (current < endTime) {
    retained.push({ start: current, end: endTime });
  }

  return retained;
};

// ===== 计算单个视频的实际播放帧数（考虑加速段和移除段） =====
export const calculateVideoPlaybackFrames = (
  duration: number,
  speedSegments?: z.infer<typeof SpeedSegmentSchema>[],
  startFrom: number = 0,
  cutSegments?: z.infer<typeof CutSegmentSchema>[]
): number => {
  const endTime = startFrom + duration;
  const retainedIntervals = getRetainedIntervals(startFrom, endTime, cutSegments);
  
  if (!speedSegments || speedSegments.length === 0) {
    // 没有加速段，直接计算保留区间的帧数
    let totalFrames = 0;
    for (const interval of retainedIntervals) {
      totalFrames += Math.ceil((interval.end - interval.start) * FPS);
    }
    return totalFrames;
  }

  // 排序加速段
  const sortedSpeedSegments = [...speedSegments].sort((a, b) => a.startTime - b.startTime);
  
  let totalFrames = 0;
  
  // 对每个保留区间计算帧数
  for (const interval of retainedIntervals) {
    let currentTime = interval.start;
    const intervalEnd = interval.end;

    for (const segment of sortedSpeedSegments) {
      const segStart = Math.max(segment.startTime, currentTime);
      const segEnd = Math.min(segment.endTime, intervalEnd);
      
      if (segStart >= intervalEnd) break;
      if (segEnd <= currentTime) continue;

      // 加速段之前的正常播放部分
      if (segStart > currentTime) {
        totalFrames += Math.ceil((segStart - currentTime) * FPS);
      }

      // 加速段
      if (segEnd > segStart) {
        totalFrames += Math.ceil((segEnd - segStart) / segment.rate * FPS);
      }

      currentTime = segEnd;
    }

    // 区间内剩余的正常播放部分
    if (currentTime < intervalEnd) {
      totalFrames += Math.ceil((intervalEnd - currentTime) * FPS);
    }
  }

  return totalFrames;
};

// ===== 计算视频帧信息 =====
export const calculateVideoFramesV8 = (
  videos: z.infer<typeof VideoItemSchemaV8>[],
  beatTimestamps?: number[],
  autoBeatAlign?: boolean,
  bpm?: number,
  previewEnabled?: boolean,
  previewDuration?: number,
  previewSlowRate?: number
) => {
  // 计算预览片段的帧数（如果启用）
  let previewFrames = 0;
  if (previewEnabled && previewDuration && previewSlowRate) {
    // 慢放后的实际时长 = 原始时长 / 慢放倍率
    const previewActualDuration = previewDuration / previewSlowRate;
    previewFrames = Math.ceil(previewActualDuration * FPS);
  }

  // 如果启用自动节拍对齐，计算时间点
  const effectiveTimestamps = autoBeatAlign 
    ? calculateAutoBeatTimestampsV8(videos, bpm)
    : beatTimestamps;

  // 如果有节拍时间点，按节拍切换
  if (effectiveTimestamps && effectiveTimestamps.length > 1) {
    const frameInfo = videos.slice(0, effectiveTimestamps.length - 1).map((_, index) => {
      const startTime = effectiveTimestamps[index];
      const endTime = effectiveTimestamps[index + 1];
      const startFrame = Math.floor(startTime * FPS) + previewFrames;
      const durationInFrames = Math.floor((endTime - startTime) * FPS);
      return {
        startFrame,
        durationInFrames,
      };
    });
    const totalFrames = Math.floor(effectiveTimestamps[effectiveTimestamps.length - 1] * FPS) + previewFrames;
    return { frameInfo, totalFrames, previewFrames };
  }

  // 否则按视频原始时长顺序播放（考虑加速段）
  // 如果启用预览，正片最后 previewDuration 秒不播放（已在预览中展示）
  const trimSeconds = previewEnabled && previewDuration ? previewDuration : 0;
  
  let currentFrame = previewFrames;
  const frameInfo = videos.map((video, index) => {
    const startFrom = video.startFrom || 0;
    // 实际播放时长 = 总时长 - startFrom
    let actualPlayDuration = (video.duration || 0) - startFrom;
    // 第一个视频截掉最后 previewDuration 秒
    if (index === 0 && trimSeconds > 0) {
      actualPlayDuration = Math.max(0, actualPlayDuration - trimSeconds);
    }
    const frames = calculateVideoPlaybackFrames(actualPlayDuration, video.speedSegments, startFrom, video.cutSegments);
    const info = {
      startFrame: currentFrame,
      durationInFrames: frames,
    };
    currentFrame += frames;
    return info;
  });
  return {
    frameInfo,
    totalFrames: currentFrame,
    previewFrames,
  };
};

// ===== 生成视频分段信息 =====
type VideoSegment = {
  videoStartFrom: number; // 视频开始时间
  segmentDuration: number; // 这段视频的原始时长
  playbackRate: number; // 播放倍率
  frameStart: number; // 在输出中的开始帧
  frameDuration: number; // 在输出中的帧数
};

const generateVideoSegments = (
  video: z.infer<typeof VideoItemSchemaV8>,
  baseFrameStart: number,
  trimEndSeconds: number = 0 // 截掉末尾的秒数
): VideoSegment[] => {
  const startFrom = video.startFrom || 0;
  const duration = Math.max(0, (video.duration || 0) - trimEndSeconds);
  const speedSegments = video.speedSegments || [];
  const cutSegments = video.cutSegments || [];
  const endTime = startFrom + duration;

  // 获取保留的时间区间
  const retainedIntervals = getRetainedIntervals(startFrom, endTime, cutSegments);
  
  if (retainedIntervals.length === 0) {
    return [];
  }

  const segments: VideoSegment[] = [];
  let currentFrameStart = baseFrameStart;

  // 对每个保留区间生成分段
  for (const interval of retainedIntervals) {
    const intervalStart = interval.start;
    const intervalEnd = interval.end;

    if (speedSegments.length === 0) {
      // 没有加速段，整个区间正常播放
      const segDuration = intervalEnd - intervalStart;
      const segFrames = Math.ceil(segDuration * FPS);
      segments.push({
        videoStartFrom: intervalStart,
        segmentDuration: segDuration,
        playbackRate: 1,
        frameStart: currentFrameStart,
        frameDuration: segFrames,
      });
      currentFrameStart += segFrames;
      continue;
    }

    // 有加速段，需要在区间内处理
    const sortedSpeedSegments = [...speedSegments].sort((a, b) => a.startTime - b.startTime);
    let currentVideoTime = intervalStart;

    for (const seg of sortedSpeedSegments) {
      const segStart = Math.max(seg.startTime, currentVideoTime);
      const segEnd = Math.min(seg.endTime, intervalEnd);

      if (segStart >= intervalEnd) break;
      if (segEnd <= currentVideoTime) continue;

      // 加速段之前的正常播放部分
      if (segStart > currentVideoTime) {
        const normalDuration = segStart - currentVideoTime;
        const normalFrames = Math.ceil(normalDuration * FPS);
        segments.push({
          videoStartFrom: currentVideoTime,
          segmentDuration: normalDuration,
          playbackRate: 1,
          frameStart: currentFrameStart,
          frameDuration: normalFrames,
        });
        currentFrameStart += normalFrames;
      }

      // 加速段
      if (segEnd > segStart) {
        const speedDuration = segEnd - segStart;
        const speedFrames = Math.ceil(speedDuration / seg.rate * FPS);
        segments.push({
          videoStartFrom: segStart,
          segmentDuration: speedDuration,
          playbackRate: seg.rate,
          frameStart: currentFrameStart,
          frameDuration: speedFrames,
        });
        currentFrameStart += speedFrames;
      }

      currentVideoTime = segEnd;
    }

    // 区间内剩余的正常播放部分
    if (currentVideoTime < intervalEnd) {
      const normalDuration = intervalEnd - currentVideoTime;
      const normalFrames = Math.ceil(normalDuration * FPS);
      segments.push({
        videoStartFrom: currentVideoTime,
        segmentDuration: normalDuration,
        playbackRate: 1,
        frameStart: currentFrameStart,
        frameDuration: normalFrames,
      });
      currentFrameStart += normalFrames;
    }
  }

  return segments;
};

// ===== 获取当前时间点的文案 =====
const getOverlayTextAtTime = (
  textOverlays: z.infer<typeof TextOverlaySchema>[] | undefined,
  currentTime: number
): string | undefined => {
  if (!textOverlays || textOverlays.length === 0) return undefined;
  
  // 按开始时间排序
  const sorted = [...textOverlays].sort((a, b) => a.startTime - b.startTime);
  
  // 找到当前时间应该展示的文案（最后一个 startTime <= currentTime 的）
  let currentText: string | undefined;
  for (const overlay of sorted) {
    if (overlay.startTime <= currentTime) {
      currentText = overlay.text;
    } else {
      break;
    }
  }
  return currentText;
};

// ===== 主组件 =====
export const HighlightVideoV8: React.FC<HighlightVideoV8Props> = ({
  videos,
  audioUrl,
  audioStartFrom = 0,
  audioVolume = 0.7,
  videoVolume = 0.1,
  videoScale = 1.6,
  beatTimestamps,
  autoBeatAlign = false,
  bpm = 99.4,
  loopBack = false,
  loopBackStartFrom = 0,
  previewEnabled = false,
  previewDuration = 3,
  previewSlowRate = 0.5,
  previewZoom = 1.8,
  previewFocusX = 50,
  previewFocusY = 30,
  title = "DOTA2",
  subtitle = "超神集锦",
  brandName = "@超神时刻",
  brandSubtitle = "GODLIKE MOMENTS",
  textOverlays,
}) => {
  // 如果启用首尾呼应，在视频列表末尾添加第一个视频
  const finalVideos = loopBack && videos.length > 0
    ? [...videos, { ...videos[0], startFrom: loopBackStartFrom }]
    : videos;
  
  const { previewFrames } = calculateVideoFramesV8(
    finalVideos, 
    beatTimestamps, 
    autoBeatAlign, 
    bpm,
    previewEnabled,
    previewDuration,
    previewSlowRate
  );

  // 计算预览片段的视频起始时间（从第一个视频的末尾截取）
  const firstVideo = videos[0];
  const previewStartTime = firstVideo ? (firstVideo.duration || 0) - previewDuration : 0;

  // 生成所有视频的分段信息
  const trimSeconds = previewEnabled && previewDuration ? previewDuration : 0;
  let currentFrameStart = previewFrames;
  const allSegments: { video: z.infer<typeof VideoItemSchemaV8>; segment: VideoSegment }[] = [];
  
  finalVideos.forEach((video, index) => {
    const trimEnd = index === 0 ? trimSeconds : 0;
    const segments = generateVideoSegments(video, currentFrameStart, trimEnd);
    segments.forEach(segment => {
      allSegments.push({ video, segment });
    });
    if (segments.length > 0) {
      const lastSeg = segments[segments.length - 1];
      currentFrameStart = lastSeg.frameStart + lastSeg.frameDuration;
    }
  });

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* 背景音乐 */}
      {audioUrl && (
        <Audio src={toHttpCdn(audioUrl)} startFrom={Math.floor(audioStartFrom * FPS)} volume={audioVolume} />
      )}

      {/* 高光预览片段 - 慢镜头 + 放大聚焦 */}
      {previewEnabled && firstVideo && previewFrames > 0 && (
        <Sequence from={0} durationInFrames={previewFrames}>
          <FullScreenVideo
            src={firstVideo.url}
            startFrom={previewStartTime}
            title={title}
            subtitle={subtitle}
            brandName={brandName}
            brandSubtitle={brandSubtitle}
            showFlash={false}
            playbackRate={previewSlowRate}
            zoom={previewZoom}
            zoomAnimated={true}
            zoomDurationFrames={previewFrames}
            focusX={previewFocusX}
            focusY={previewFocusY}
            volume={videoVolume}
          />
        </Sequence>
      )}

      {/* 按顺序播放视频分段 */}
      {allSegments.map(({ video, segment }, index) => (
        <Sequence
          key={index}
          from={segment.frameStart}
          durationInFrames={segment.frameDuration}
        >
          <FullScreenVideo
            src={video.url}
            startFrom={segment.videoStartFrom}
            title={title}
            subtitle={subtitle}
            brandName={brandName}
            brandSubtitle={brandSubtitle}
            showFlash={false}
            videoScale={videoScale}
            playbackRate={segment.playbackRate}
            textOverlays={textOverlays}
            outputTimeOffset={segment.frameStart / FPS}
            volume={videoVolume}
          />
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

// 导出视频配置供 Root 使用
export const HIGHLIGHT_VIDEO_V8_CONFIG = {
  fps: FPS,
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
};
